import { useState, useEffect, useCallback } from 'react';
import {
  Play, Loader2, RefreshCw, MessageSquare,
  Send, FileText, CheckCircle2,
  AlertTriangle, Clock, BarChart3,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ingestionService from '../api';

function ScoreBadge({ score }) {
  if (score == null) return null;
  const pct = Math.round(score <= 1 ? score * 100 : score);
  const color = pct >= 75 ? 'bg-green-100 text-green-700' : pct >= 50 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700';
  return <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${color}`}>{pct}%</span>;
}

function RunRow({ run, onSelect, selected }) {
  const date = run.finished_at || run.started_at;
  const dateStr = date ? new Date(date).toLocaleString() : '—';
  return (
    <button
      className={`w-full flex items-center gap-3 px-4 py-3 text-left border-b border-gray-100 hover:bg-blue-50/40 transition-colors ${selected ? 'bg-blue-50' : ''}`}
      onClick={() => onSelect(run)}
    >
      {run.status === 'completed' ? (
        <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
      ) : run.status === 'running' ? (
        <Loader2 className="w-4 h-4 text-blue-500 animate-spin shrink-0" />
      ) : (
        <AlertTriangle className="w-4 h-4 text-yellow-500 shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-800 truncate">{run.folder_path || `Run #${run.id}`}</p>
        <p className="text-xs text-gray-400">{dateStr} · {run.total_documents ?? 0} docs</p>
      </div>
      <ScoreBadge score={run.avg_quality_score} />
    </button>
  );
}

function RunDetail({ run }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!run) return;
    setLoading(true);
    ingestionService.getQualityRun(run.id)
      .then(setDetail)
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [run]);

  if (!run) return null;
  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-blue-500" /></div>;
  if (!detail) return <p className="text-xs text-gray-400 py-4">Could not load run details.</p>;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div>
          <p className="text-sm font-semibold text-gray-900">{detail.folder_path || `Run #${detail.id}`}</p>
          <p className="text-xs text-gray-400">{detail.total_documents} documents · status: {detail.status}</p>
        </div>
        <div className="ml-auto"><ScoreBadge score={detail.avg_quality_score} /></div>
      </div>
      {detail.summary && <p className="text-xs text-gray-600 bg-gray-50 rounded p-2">{detail.summary}</p>}
      {(detail.documents || []).map(doc => (
        <div key={doc.id} className="bg-white border border-gray-200 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <FileText className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-800 truncate flex-1">{doc.filename}</span>
            <ScoreBadge score={doc.quality_score ?? doc.score} />
          </div>
          {(doc.questions || []).slice(0, 3).map((q, i) => (
            <p key={i} className="text-xs text-gray-500 pl-6">• {q.question || q}</p>
          ))}
        </div>
      ))}
    </div>
  );
}

function QAPanel() {
  const [question, setQuestion] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const data = await ingestionService.getQuestionHistory(20);
      setHistory(Array.isArray(data) ? data : []);
    } catch { setHistory([]); }
    finally { setLoadingHistory(false); }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const handleAsk = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;
    setSubmitting(true);
    setAnswer(null);
    try {
      const res = await ingestionService.askQuestion(question.trim());
      setAnswer(res);
      setQuestion('');
      loadHistory();
    } catch (err) {
      setAnswer({ error: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={handleAsk} className="flex gap-2">
        <input
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          placeholder="Ask a question about your documents…"
          value={question}
          onChange={e => setQuestion(e.target.value)}
          disabled={submitting}
        />
        <button
          type="submit"
          disabled={submitting || !question.trim()}
          className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Ask
        </button>
      </form>

      {answer && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
          {answer.error ? (
            <p className="text-sm text-red-600">{answer.error}</p>
          ) : (
            <>
              <div className="text-sm text-gray-900 prose prose-sm max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{answer.answer || ''}</ReactMarkdown>
              </div>
              {(answer.sources || []).length > 0 && (
                <div className="pt-2 border-t border-blue-200">
                  <p className="text-xs font-medium text-gray-500 mb-1">Sources</p>
                  {answer.sources.map((src, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
                      <FileText className="w-3 h-3 text-gray-400" />
                      <span className="flex-1 truncate">{src.filename}</span>
                      {src.relevance != null && (
                        <span className="font-medium text-primaryDark">{(src.relevance * 100).toFixed(1)}%</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-medium text-gray-500">Recent Questions</p>
          <button onClick={loadHistory} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
        {loadingHistory ? (
          <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-gray-400" /></div>
        ) : history.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-4">No questions asked yet.</p>
        ) : (
          <div className="space-y-2">
            {history.map(item => (
              <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-3">
                <p className="text-sm font-medium text-gray-800">{item.question}</p>
                <p className="text-xs text-gray-500 mt-1 line-clamp-2">{item.answer}</p>
                <div className="flex items-center gap-2 mt-1">
                  <Clock className="w-3 h-3 text-gray-300" />
                  <span className="text-xs text-gray-400">{item.asked_at ? new Date(item.asked_at).toLocaleString() : ''}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function QualityAssessment() {
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState('');
  const [running, setRunning] = useState(false);
  const [runs, setRuns] = useState([]);
  const [loadingRuns, setLoadingRuns] = useState(true);
  const [selectedRun, setSelectedRun] = useState(null);
  const [activePanel, setActivePanel] = useState('runs');

  const loadFolders = useCallback(async () => {
    try {
      const data = await ingestionService.listAssessableFolders();
      const arr = Array.isArray(data) ? data : [];
      setFolders(arr);
      if (arr.length > 0 && !selectedFolder) setSelectedFolder(String(arr[0].id));
    } catch { setFolders([]); }
  }, [selectedFolder]);

  const loadRuns = useCallback(async () => {
    setLoadingRuns(true);
    try {
      const data = await ingestionService.listQualityRuns();
      setRuns(Array.isArray(data) ? data : []);
    } catch { setRuns([]); }
    finally { setLoadingRuns(false); }
  }, []);

  useEffect(() => {
    loadFolders();
    loadRuns();
  }, [loadFolders, loadRuns]);

  const handleRun = async () => {
    if (!selectedFolder) return;
    setRunning(true);
    try {
      await ingestionService.runQualityAssessment(selectedFolder);
      await loadRuns();
    } catch (err) {
      alert(`Assessment failed: ${err.message}`);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold text-gray-900">Quality Assessment</h2>
        <p className="text-xs text-gray-500 mt-0.5">Run quality checks on scanned folders or ask questions about your documents</p>
      </div>

      {/* Run panel */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
        <p className="text-sm font-medium text-gray-700 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-primaryDark" /> Run Assessment</p>
        <div className="flex gap-2 flex-wrap">
          <select
            className="flex-1 min-w-[160px] border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={selectedFolder}
            onChange={e => setSelectedFolder(e.target.value)}
          >
            <option value="">Select a folder…</option>
            {folders.map(f => (
              <option key={f.id} value={String(f.id)}>{f.path || f.full_path || `Folder #${f.id}`}</option>
            ))}
          </select>
          <button
            onClick={handleRun}
            disabled={running || !selectedFolder}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            {running ? 'Running…' : 'Run Assessment'}
          </button>
        </div>
      </div>

      {/* Tabs: Runs history / Q&A */}
      <div className="flex gap-1 border-b border-gray-200">
        {[
          { id: 'runs', label: 'Assessment History', icon: BarChart3 },
          { id: 'qa', label: 'Ask a Question', icon: MessageSquare },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActivePanel(id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activePanel === id ? 'border-blue-600 text-primaryDark' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon className="w-4 h-4" />{label}
          </button>
        ))}
      </div>

      {activePanel === 'runs' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100">
              <p className="text-xs font-medium text-gray-500">Assessment Runs</p>
              <button onClick={loadRuns} className="text-gray-400 hover:text-gray-600"><RefreshCw className="w-3.5 h-3.5" /></button>
            </div>
            {loadingRuns ? (
              <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-blue-500" /></div>
            ) : runs.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-8">No runs yet. Run an assessment above.</p>
            ) : (
              runs.map(run => (
                <RunRow key={run.id} run={run} onSelect={setSelectedRun} selected={selectedRun?.id === run.id} />
              ))
            )}
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            {selectedRun ? (
              <RunDetail run={selectedRun} />
            ) : (
              <p className="text-xs text-gray-400 text-center py-8">Select a run to view details.</p>
            )}
          </div>
        </div>
      )}

      {activePanel === 'qa' && <QAPanel />}
    </div>
  );
}
